import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { ApolloError } from "apollo-boost";
import { Field, FieldProps, Form, Formik } from "formik";
import CircularProgress from "@material-ui/core/CircularProgress";
import LaunchIcon from '@material-ui/icons/Launch';
import * as Yup from "yup";
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import { CREATE_CUSTOMER } from "../../../../../graphql/customers/createCustomerMutation";
import { useSnackBar } from "../../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../../SnackbarWrapper/SnackbarWrapper";

import styles from './index.module.css';
import { createStyles, Theme, withStyles } from "@material-ui/core/styles";

import { buildFieldRequiredMap, buildFieldVisibilityMap, DATE_TYPE, defaultWebsiteIndividualFields, formatGraphQLErrorMessage } from "../../../../utils";
import { Box, Button, Checkbox, CheckboxProps, Fab, FormControl, FormControlLabel, Grid, IconButton, makeStyles, MenuItem, Paper, Radio, RadioGroup, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { IAppState } from '../../../../../store';
import { useSelector } from 'react-redux';
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { IBookingQuestionInput, ICustomerInfoInput, IAddress, IBookingCreateInput, BookingType } from '../../../../../reducers/bookings/types';
import { ANSWER_TYPES, BOOKING_CATEGORY, IBookingQuestion } from '../../../../../reducers/website/types';
import Hidden from '@material-ui/core/Hidden';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';
import { AccountType, IUser, UserRole } from '../../../../../reducers/auth/types';
import { getDefaultCountryCode, getLocalisedLicense, getLocalizedBookingSyntex, getLocalizedDateFormat } from "../../../../../utils/localized.syntex";
import AddressPicker from "../../../../AddressPicker/AddressPicker";
import AddNewDriver, { IDriverRow } from "./AddNewDriver";
import { SearchForm } from "../../../CustomerPortal/SearchForm";
import DeleteIcon from '@material-ui/icons/Delete';
import _, { debounce } from "lodash"
import { getMergedPreBookingQuestions, NameTitleOptions } from "../../utils";
import { useTranslation } from "react-i18next";
import { GET_CUSTOM_FORM_BY_TYPE } from "../../../../../graphql/customers/getConsumerCustomForm";
import { downloadTermsAndConditions } from "../../../../../utils/getSignedUrl";
import { Autocomplete } from "@material-ui/lab";
import { SEARCH_PROJECT_IDS } from "../../../../../graphql/bookings/getSearchProjectIdsQuery";
import FlatPickerBar from "../../../../FlatPicker";
import { DateTime as d } from "luxon";
import { TranslationKeys } from "../../../../../utils/translations/types";
import { Gender } from "../../../../../reducers/drivers/const";

const WhiteCircularProgress = withStyles(() =>
  createStyles({
    root: {
      color: '#fff !important',
      width: 30,
      height: 30
    },
  }),
)(CircularProgress);

interface IProps {
  updateCustomer: (data: { customer?: string, businessCustomer?: string, approvedDrivers?: string[], preBookingQuestions?: IBookingQuestionInput[], customerNote?: string, costCenter?: string, projectId?: string }) => void;
  onPrevious(values: ICustomerInfoInput): void;
  customerInfo: ICustomerInfoInput;
  branchId?: string;
  bookingData?: IBookingCreateInput;
  driverCount?: number;
  updateBookingDrivers: (data: IDriverRow[]) => void;
  bookingDrivers?: IDriverRow[];
}
const StyledInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiOutlinedInput-input': {
        padding: '12px 14px',
        height: '20px',
      },
      '& .MuiOutlinedInput-multiline': {
        padding: '12px 14px'
      },
      '& .MuiOutlinedInput-root fieldset': {
        borderColor: '#e2e2e2',
        borderRadius: 8
      },
      '& .MuiSelect-select:focus': {
        backgroundColor: 'transparent'
      },
      '& .MuiSelect-select': {
        padding: '12px 14px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
      },
    },
  }),
)(TextField);

const useStyles = makeStyles((theme) => ({
  fieldLabel: {
    fontSize: "12px",
    color: "#666",
    marginBottom: "6px",
    display: "block",
    fontWeight: 500,
    lineHeight: "16px",
  },
  fieldLabelError: {
    color: "#D13135",
  },
  requiredStar: {
    color: "#D13135",
    marginLeft: 3,
  },
  inputBorderWrap: {
    border: "1px solid #e2e2e2",
    borderRadius: 8,
    minHeight: "44px",
    display: "flex",
    alignItems: "center",
  },
  inputBorderWrapError: {
    borderColor: "#D13135",
  },
  inputError: {
    fontSize: "11px",
    color: "#D13135",
    marginTop: 4,
    minHeight: 16,
  },
}));

const ColoredCheckbox = withStyles({
  root: {
    color: 'var(--primary-color)',
    '&$checked': {
      color: 'var(--primary-color)',
    },
  },
  checked: {},
})((props: CheckboxProps) => <Checkbox color="default" {...props} />);

export interface IAdditionalDriver {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  license: {
    authority: string;
    licenseNumber: string;
    dateOfExpiry: string;
    images: (string | object | undefined)[];
  };
  phoneNumber: {
    phone: string;
    country: string;
  };
  location: IAddress;
}

const SimpleCustomerView: React.FC<IProps> = (props) => {
  const snackbar = useSnackBar();
  const classes = useStyles();
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { organisation } = website
  const { country } = website.organisation.address;
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const authState = useSelector((state: IAppState) => state.authReducer)
  const organisationData = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.organisation);
  const branchData = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.branches);
  const [driverCount, setDriverCount] = useState<number>(0);
  const [additionalDriverQuantity, setAdditionalDriverQuantity] = useState<number>(0);
  const [costCenter, setCostCenter] = useState<string>(props.bookingData?.costCenter || "");
  const [projectId, setProjectId] = useState<string>(props.bookingData?.projectId || "");
  const [availableProjectIds, setAvailableProjectIds] = useState<string[]>([]);
  const [driverList, setDriverList] = useState<any[]>([]);
  const { t } = useTranslation();
  const [requiredMap, setRequiredMap] = useState<Record<string, boolean>>(
    buildFieldRequiredMap(defaultWebsiteIndividualFields)
  );
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>(
    buildFieldVisibilityMap(defaultWebsiteIndividualFields)
  );
  const [queryInputValue, setQueryInputValue] = useState("");
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [loadingProjectIds, setLoadingProjectIds] = useState<boolean>(false);

  const [getCustomFormByType] = useLazyQuery(GET_CUSTOM_FORM_BY_TYPE, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.getConsumerCustomFormType?.config?.website?.fields.length) {
        const customMap = buildFieldRequiredMap(data.getConsumerCustomFormType.config.website.fields);
        const customVisibilityMap = buildFieldVisibilityMap(data.getConsumerCustomFormType.config.website.fields);
        setRequiredMap(customMap);
        setVisibilityMap(customVisibilityMap);
      } else {
        const defaultMap = buildFieldRequiredMap(defaultWebsiteIndividualFields);
        const defaultVisibilityMap = buildFieldVisibilityMap(defaultWebsiteIndividualFields);
        setRequiredMap(defaultMap);
        setVisibilityMap(defaultVisibilityMap);
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
      const defaultMap = buildFieldRequiredMap(defaultWebsiteIndividualFields);
      const defaultVisibilityMap = buildFieldVisibilityMap(defaultWebsiteIndividualFields);
      setRequiredMap(defaultMap);
      setVisibilityMap(defaultVisibilityMap);
    }
  });

  const [searchProjectIds, { loading: loadingProjectIdsqyery, data: searchProjectIdData }] =
    useLazyQuery(SEARCH_PROJECT_IDS, {
      fetchPolicy: "network-only",
    });

  useEffect(() => {
    getCustomFormByType({
      variables: {
        organisationId: organisationData.id,
        formType: "INDIVIDUAL",
        atBranchLevel: false
      }
    })
  }, [])

  const [createCustomer, { loading: creatingCustomer }] = useMutation(CREATE_CUSTOMER, {});

  const onSubmit = (values: ICustomerInfoInput) => {
    const driversId = driverList.map(driver => driver.id)
    const {
      agreeToTerms: _agreeToTerms,
      preBookingQuestions: _preBookingQuestions,
      customerNote,
      deliveryAddress: _deliveryAddress,
      ...newCustomer
    } = values;
    const preBookingQuestions = values.preBookingQuestions.map((bq) => ({
      question: bq.question,
      answerType: bq.answerType,
      answer: bq.answer?.toString() || ''
    }));

    if (user && user.customerId && user.businesscustomers) { //user is logged in as approved driver
      props.updateCustomer({ businessCustomer: user.businesscustomers.id, approvedDrivers: driversId, preBookingQuestions, customerNote });
    } else if (user && user.customerId) {
      props.updateCustomer({ customer: user.customerId, approvedDrivers: driversId, preBookingQuestions, customerNote, costCenter, projectId });
    } else if (user && user.businesscustomers) {
      props.updateCustomer({ businessCustomer: user.businesscustomers.id, approvedDrivers: driversId, preBookingQuestions, customerNote });
    } else {
      createCustomer({ variables: { customer: newCustomer } })
        .then((result: any) => {
          const { data } = result
          if (data && data.consumerCreateCustomer) {
            const driverArr: string[] = [];
            if (data.consumerCreateCustomer.approvedDrivers) {
              data.consumerCreateCustomer.approvedDrivers.forEach((driver: any) => {
                driverArr.push(driver.id);
              });
            }
            props.updateCustomer({ customer: data.consumerCreateCustomer.id, preBookingQuestions, customerNote });
          }
        })
        .catch((error: ApolloError) => {
          snackbar({
            message: formatGraphQLErrorMessage(error.message),
            variant: SnackBarVariant.ERROR
          });
        });
    }
  };

  const tt = (key: string, options?: any): string => {
    return (t(key as any, options) as unknown as string) || key;
  };

  const createDynamicCustomerSchema = (
    requiredMap: Record<string, boolean>,
    t: (key: string, options?: Record<string, any>) => string
  ) => {
    const getLabel = (labelKey: string) => t(labelKey) ?? labelKey;

    const createConditionalString = (
      key: string,
      errorKey: TranslationKeys,
      interpolationValues?: Record<string, string | number>
    ) =>
      requiredMap[key]
        ? interpolationValues
          ? Yup.string().required(t(errorKey, { ...interpolationValues }))
          : Yup.string().required(t(errorKey))
        : Yup.string();

    const createConditionalEmail = (fieldKey: string, labelKey: string) => {
      const base = Yup.string().email(
        t("validation:pleaseEnterAValidEmail", { field: getLabel(labelKey) })
      );
      return requiredMap[fieldKey]
        ? base.required(t("validation:emailIsRequired", { field: getLabel(labelKey) }))
        : base;
    };

    const createConditionalPhone = (fieldKey: string, labelKey: string) => {
      const base = Yup.string().test(
        "is-valid-phone",
        t("root:invalidPhoneNumber"),
        (value) => {
          if (!value) return true;
          return isValidPhoneNumber(value);
        }
      );
      return requiredMap[fieldKey]
        ? base.required(t("validation:phoneNumberRequired", { field: getLabel(labelKey) }))
        : base;
    };

    const requiredString = (key: string, label: string) => {
      return requiredMap[key]
        ? Yup.string().required(tt("validation:required", { field: tt(label) }))
        : Yup.string().nullable();
    };

    const addressSchema = () => {
      if (!requiredMap.location) {
        return Yup.object({
          fullAddress: Yup.string(),
          country: Yup.string(),
          state: Yup.string(),
          city: Yup.string(),
          street: Yup.string(),
          zipcode: Yup.string(),
        });
      }
      return Yup.object({
        fullAddress: Yup.string().required(tt("validation:required", { field: tt("root:address") })),
        country: Yup.string().required(tt("validation:required", { field: tt("root:country") })),
        state: Yup.string().required(tt("validation:required", { field: tt("root:state") })),
        city: Yup.string().required(tt("validation:required", { field: tt("root:city") })),
        street: Yup.string().required(tt("validation:required", { field: tt("root:street") })),
        zipcode: Yup.string(),
      });
    };

    return Yup.object().shape({
      title: requiredString("title", "root:title"),
      firstName: createConditionalString("firstName", "validation:firstNameRequired"),
      middleName: createConditionalString("middleName", "validation:middleNameRequired"),
      lastName: createConditionalString("lastName", "validation:lastNameRequired"),
      nationality: requiredString("nationality", "root:nationality"),
      email: createConditionalEmail("email", "root:emailAddress"),
      secondaryEmail: createConditionalEmail("secondaryEmail", "root:secondaryEmailAddress"),
      phoneNumber: Yup.object().shape({
        phone: createConditionalPhone("phoneNumber", "root:phoneNumber"),
        country: Yup.string(),
      }),
      secondaryPhoneNumber: Yup.object().shape({
        phone: createConditionalPhone("secondaryPhoneNumber", "root:secondaryPhoneNumber"),
        country: Yup.string(),
      }),
      location: addressSchema(),
      nationalInsuranceNumber: createConditionalString("nationalInsuranceNumber", "validation:nationalInsuranceNumberRequired"),
      passportNumber: requiredString("passportNumber", "root:passportNumber"),
      profession: createConditionalString("profession", "validation:professionIsRequired"),
      dateOfBirth: requiredString("dateOfBirth", "root:dateOfBirth"),
      gender: createConditionalString("gender", "validation:genderRequired"),
      customerPCONumber: createConditionalString("customerPCONumber", "validation:customerPCONumberRequired"),
      accountNumber: createConditionalString("accountNumber", "validation:accountNumberRequired"),
      license: Yup.object().shape({
        licenseNumber: createConditionalString("licenseNumber", "validation:licenceNumberRequired", { licenceSyntax: getLocalisedLicense(country) }),
        authority: createConditionalString("authority", "validation:authorityRequired"),
        dateOfIssue: requiredString("dateOfIssue", "root:drivingLicenceIssueDate"),
        dateOfExpiry: requiredString("dateOfExpiry", "root:drivingLicenceExpiryDate"),
      }),
      pcoIssueDate: requiredString("pcoIssueDate", "root:pcoIssueDate"),
      pcoExpiryDate: requiredString("pcoExpiryDate", "root:pcoExpiryDate"),
    });
  };

  const schema = React.useMemo(() => createDynamicCustomerSchema(requiredMap, t), [requiredMap, t, country]);

  const ninetyFiveYearsOldDate = getLocalizedDateFormat(
    country,
    d.now().minus({ year: 95 }).toUTC().toISO(),
    DATE_TYPE.CONDENSED
  );
  const eighteenYearsOldDate = getLocalizedDateFormat(
    country,
    d.now().minus({ year: 18 }).toUTC().toISO(),
    DATE_TYPE.CONDENSED
  );

  const validateAnswer = (value: any, required: boolean, expected?: string) => {
    let error;
    if (required) {
      if (expected === "TRUE" && !value) error = t("misc:error");
      if (expected === "FALSE" && value) error = t("misc:error");
      if (expected === "TEXT" && (!value || value.trim() === "")) error = t("misc:error");
      if (expected === "YES" && value !== "YES") error = t("misc:error");
      if (expected === "NO" && value !== "NO") error = t("misc:error");
    }
    return error;
  }

  let mergedPreBookingQuestions: IBookingQuestion[] = []
  let preBookingQuestionsFromBranch: IBookingQuestion[] = [];
  const branch = branchData.find(branch => branch.id === props.branchId);
  if (branch) {
    branch.preBookingQuestions && branch.preBookingQuestions?.length && branch.preBookingQuestions.forEach((bq) => {
      preBookingQuestionsFromBranch.push({ ...bq, answer: "" });
    })
  }

  let preBookingQuestionsFromBooking: IBookingQuestionInput[] = []
  if (props.customerInfo && props.customerInfo.preBookingQuestions) {
    preBookingQuestionsFromBooking = props.customerInfo.preBookingQuestions
  }
  mergedPreBookingQuestions = getMergedPreBookingQuestions(preBookingQuestionsFromBooking, preBookingQuestionsFromBranch)

  const getNextStepText = (bookingType: BookingType) => {
    if (bookingType === BookingType.TEST_DRIVE) return t("root:confirmBooking");
    if (website.organisation.closeGroupSharingEnabled) return t("root:proceed");
    return t('root:continue');
  }

  useEffect(() => {
    if (props.bookingData && props.bookingData.addonRequirements) {
      const addon = props.bookingData.addonRequirements.find((item: any) => item.name === "ADDITIONAL_DRIVER");
      if (addon) setAdditionalDriverQuantity(addon.quantity);
    }
  }, [props.bookingData?.addonRequirements]);

  useEffect(() => {
    if (props.bookingDrivers?.length) {
      setDriverList(props.bookingDrivers)
      setDriverCount(props.bookingDrivers.length)
    }
  }, [props.bookingDrivers])

  const addExistingDriver = (data: IDriverRow) => {
    const existingDriver = driverList.some((driver) => driver.email === data.email)
    if (existingDriver) {
      return snackbar({ message: t("snackBar:selectedDriverAlreadyAdded"), variant: SnackBarVariant.ERROR });
    } else {
      if (data) {
        if (driverCount < additionalDriverQuantity) {
          const driverData: any[] = [...driverList, data];
          setDriverList(driverData)
          setDriverCount((prevCount) => prevCount + 1);
          props.updateBookingDrivers(driverData)
          snackbar({ message: t("snackBar:driverSavedSuccessfully"), variant: SnackBarVariant.SUCCESS })
        } else {
          snackbar({ message: t("snackBar:additionalDriverCannotBeMore"), variant: SnackBarVariant.ERROR });
        }
      }
    }
  }

  const handleRemoveDriver = (newDriver: IDriverRow) => {
    const filteredDrivers = driverList.filter((driver) => driver.email !== newDriver.email)
    setDriverList(filteredDrivers)
    setDriverCount((prevCount) => prevCount - 1);
    props.updateBookingDrivers(filteredDrivers)
  }

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        if (query.length < 3) {
          setProjectIds([]);
          return;
        }
        setLoadingProjectIds(true);
        searchProjectIds({ variables: { organisationId: organisation.id, query } });
      }, 300),
    [organisation.id]
  );

  useEffect(() => { debouncedSearch(queryInputValue); }, [queryInputValue]);

  useEffect(() => {
    if (searchProjectIdData?.searchProjectIds) {
      setProjectIds(searchProjectIdData.searchProjectIds);
      setLoadingProjectIds(false);
    }
  }, [searchProjectIdData]);

  const FieldLabel = ({
    label,
    required,
    hasError,
  }: {
    label: string;
    required?: boolean;
    hasError?: boolean;
  }) => (
    <label className={`${classes.fieldLabel} ${hasError ? classes.fieldLabelError : ""}`}>
      {label}
      {required && <span className={classes.requiredStar}>*</span>}
    </label>
  );

  return (
    <Formik
      validationSchema={!user?.id && schema}
      initialValues={{
        ...props.customerInfo,
        agreeToTerms: false,
        preBookingQuestions: mergedPreBookingQuestions,
      }}
      onSubmit={(values, { setSubmitting }) => {
        if (values.location.fullAddress && (!values.location.street || !values.location.zipcode)) {
          return snackbar({
            message: t("validation:streetZipcodeRequired"),
            variant: SnackBarVariant.ERROR,
          });
        }
        onSubmit(values);
        setSubmitting(false);
      }}
    >
      {(formikProps) => (
        <Form className={styles.container}>
          <Grid container>
            <Grid item container xs={12}>
              {(user && user.id) ?
                <Grid item xs={12}>
                  <Typography variant="body1" className="padding-bottom text-center semi-bold">
                    {getLocalizedBookingSyntex(country)} {t("root:for")}
                  </Typography>
                  {authState.accountType === AccountType.INDIVIDUAL ? (
                    <div className={styles.box}>
                      <div className={styles.avatar}>
                        <span className="font-large bold">
                          {user.firstName.slice(0, 1).toUpperCase()}{user.lastName.slice(0, 1).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex fill col-flex margin-left">
                        <Typography variant="h3" className="semi-bold" style={{ marginLeft: 1, marginBottom: 4 }}>
                          {user.firstName} {user.lastName}
                        </Typography>
                        <span className="flex cross-center font-medium opacity50" style={{ flexWrap: 'wrap' }}>
                          <span className="flex cross-center" style={{ marginRight: '10px', display: 'flex', alignItems: 'center' }}>
                            <EmailIcon style={{ fontSize: 17, marginRight: 3 }} />
                            <span>{user.email}</span>
                          </span>
                          <span className="flex cross-center" style={{ marginRight: '10px', display: 'flex', alignItems: 'center' }}>
                            <PhoneIcon style={{ fontSize: 17, marginRight: 3, marginLeft: 10 }} />
                            {user.customer?.phoneNumber?.phone ? (
                              <span>{user.customer?.phoneNumber.phone}</span>
                            ) : (
                              <PhoneInput
                                international={false}
                                defaultCountry={getDefaultCountryCode(website.organisation.address.country)}
                                placeholder={t("root:phonePlaceholder")}
                                value={formikProps.values.phoneNumber?.phone}
                                onChange={(val) => formikProps.setFieldValue('phoneNumber.phone', val)}
                                onCountryChange={(val) => formikProps.setFieldValue('phoneNumber.country', val)}
                              />
                            )}
                          </span>
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.box}>
                      <div className="flex fill col-flex">
                        <Typography variant="h3" className="semi-bold">{user.businesscustomers?.businessName || ""}</Typography>
                        <Typography variant="body1" className="opacity50" style={{ marginBottom: 15 }}>{t("root:businessAccount")}</Typography>
                        <span className="flex cross-center font-medium opacity50" style={{ flexWrap: 'wrap' }}>
                          <span className="margin-right">Contact: {user.firstName} {user.lastName}</span>
                          <span className="flex cross-center" style={{ marginRight: '10px', display: 'flex', alignItems: 'center' }}>
                            <EmailIcon style={{ fontSize: 17, marginRight: 3 }} />
                            <span>{user.email}</span>
                          </span>
                          <span className="flex cross-center" style={{ marginRight: '10px', display: 'flex', alignItems: 'center' }}>
                            <PhoneIcon style={{ fontSize: 17, marginRight: 3 }} />
                            {user.phoneNumber?.phone ? (
                              <span>{user.phoneNumber.phone}</span>
                            ) : (
                              <PhoneInput
                                international={false}
                                defaultCountry={getDefaultCountryCode(website.organisation.address.country)}
                                placeholder={t("root:phonePlaceholder")}
                                value={formikProps.values.phoneNumber?.phone}
                                onChange={(val) => formikProps.setFieldValue('phoneNumber.phone', val)}
                                onCountryChange={(val) => formikProps.setFieldValue('phoneNumber.country', val)}
                              />
                            )}
                          </span>
                        </span>
                      </div>
                    </div>
                  )}
                </Grid>
                :
                <Grid container item spacing={2}>

                  {/* ✅ Title */}
                  {visibilityMap.title && (
                    <Grid item xs={12} sm={6}>
                      <Field name="title">
                        {({ field, form, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:title")} required={requiredMap.title} hasError={touched && Boolean(error)} />
                            <StyledInput
                              select
                              variant="outlined"
                              {...field}
                              placeholder={t("root:title")}
                              fullWidth
                              error={touched && Boolean(error)}
                              onChange={(e: any) => form.setFieldValue("title", e.target.value)}
                            >
                              {NameTitleOptions.map((item: any) => (
                                <MenuItem key={item} value={item}>{item}</MenuItem>
                              ))}
                            </StyledInput>
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ First Name */}
                  {visibilityMap.firstName && (
                    <Grid item xs={12} sm={6}>
                      <Field name="firstName">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:firstName")} required={requiredMap.firstName} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:enterFirstName")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {visibilityMap.middleName && (
                    <Grid item xs={12} sm={6}>
                      <Field name="middleName">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:middleName")} required={requiredMap.middleName} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:enterMiddleName")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Last Name */}
                  {visibilityMap.lastName && (
                    <Grid item xs={12} sm={6}>
                      <Field name="lastName">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:lastName")} required={requiredMap.lastName} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:enterLastName")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Gender */}
                  {visibilityMap.gender && (
                    <Grid item xs={12} sm={6}>
                      <Field name="gender">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:gender")} required={requiredMap.gender} hasError={touched && Boolean(error)} />
                            <StyledInput
                              select
                              variant="outlined"
                              {...field}
                              fullWidth
                              error={touched && Boolean(error)}
                            >
                              {Object.values(Gender).map((element: string, key: number) => (
                                <MenuItem value={element} key={key}>
                                  {element.replace(/_/g, " ")}
                                </MenuItem>
                              ))}
                            </StyledInput>
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Email */}
                  {visibilityMap.email && (
                    <Grid item xs={12} sm={6}>
                      <Field name="email">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:email")} required={requiredMap.email} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:enterYourMail")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Secondary Email */}
                  {visibilityMap.secondaryEmail && (
                    <Grid item xs={12} sm={6}>
                      <Field name="secondaryEmail">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:secondaryEmailAddress")} required={requiredMap.secondaryEmail} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:enterYourMail")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Phone Number */}
                  {visibilityMap.phoneNumber && (
                    <Grid item xs={12} sm={6}>
                      <Field component={TextField} name={"phoneNumber.phone"} required={requiredMap.phoneNumber}>
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <div>
                            <FieldLabel label={t("root:phoneNumber")} required={requiredMap.phoneNumber} hasError={touched && Boolean(error)} />
                            <div className={`${classes.inputBorderWrap} ${touched && Boolean(error) ? classes.inputBorderWrapError : ""}`}>
                              <PhoneInput
                                {...field}
                                international={false}
                                defaultCountry={getDefaultCountryCode(website.organisation.address.country)}
                                placeholder={t("root:phonePlaceholder")}
                                className={(touched && Boolean(error)) ? "PhoneInputInputError" : ""}
                                name={"phoneNumber.phone"}
                                onChange={(val) => formikProps.setFieldValue('phoneNumber.phone', val)}
                                onCountryChange={(val) => formikProps.setFieldValue('phoneNumber.country', val)}
                              />
                            </div>
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </div>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Secondary Phone Number */}
                  {visibilityMap.secondaryPhoneNumber && (
                    <Grid item xs={12} sm={6}>
                      <Field component={TextField} name={"secondaryPhoneNumber.phone"} required={requiredMap.secondaryPhoneNumber}>
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <div>
                            <FieldLabel label={t("root:secondaryPhoneNumber")} required={requiredMap.secondaryPhoneNumber} hasError={touched && Boolean(error)} />
                            <div className={`${classes.inputBorderWrap} ${touched && Boolean(error) ? classes.inputBorderWrapError : ""}`}>
                              <PhoneInput
                                {...field}
                                international={false}
                                defaultCountry={getDefaultCountryCode(website.organisation.address.country)}
                                placeholder={t("root:phonePlaceholder")}
                                className={(touched && Boolean(error)) ? "PhoneInputInputError" : ""}
                                name={"secondaryPhoneNumber.phone"}
                                onChange={(val) => formikProps.setFieldValue('secondaryPhoneNumber.phone', val)}
                                onCountryChange={(val) => formikProps.setFieldValue('secondaryPhoneNumber.country', val)}
                              />
                            </div>
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </div>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Licence Number */}
                  {visibilityMap.licenseNumber && (
                    <Grid item xs={12} sm={6}>
                      <Field name="license.licenseNumber">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:drivingLicenceNumber")} required={requiredMap.licenseNumber} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:enterDrivingLicenceNumber")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Licence Authority */}
                  {visibilityMap.authority && (
                    <Grid item xs={12} sm={6}>
                      <Field name="license.authority">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:licenceIssuedBy", { licenceSyntax: getLocalisedLicense(country) })} required={requiredMap.authority} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:issuedBy")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Date of Birth */}
                  {visibilityMap.dateOfBirth && (
                    <Grid item xs={12} sm={6}>
                      <Field name="dateOfBirth">
                        {({ field, form, meta: { touched, error } }: FieldProps) => {
                          const showErr = Boolean(error) && (touched || form.submitCount > 0);
                          return (
                          <>
                            <FieldLabel label={t("root:dateOfBirth")} required={requiredMap.dateOfBirth} hasError={showErr} />
                            <div className={`${classes.inputBorderWrap} ${showErr ? classes.inputBorderWrapError : ""}`}>
                              <FlatPickerBar
                                enableTime={false}
                                handleDateChange={(value: Date) => {
                                  if (!isNaN(value.getTime())) {
                                    form.setFieldValue("dateOfBirth", d.fromJSDate(value).toUTC().toISO()!);
                                  } else {
                                    form.setFieldValue("dateOfBirth", "");
                                  }
                                }}
                                identifier="dateOfBirth"
                                placeholderValue={t("root:dateOfBirth")}
                                value={field.value ? getLocalizedDateFormat(country, field.value, DATE_TYPE.CONDENSED) : ""}
                                minDate={ninetyFiveYearsOldDate}
                                maxDate={eighteenYearsOldDate}
                                country={country}
                                disableWrapper={true}
                              />
                            </div>
                            <Typography variant="body1" className={classes.inputError}>{showErr ? error : null}</Typography>
                          </>
                          );
                        }}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Licence Issue Date */}
                  {visibilityMap.dateOfIssue && (
                    <Grid item xs={12} sm={6}>
                      <Field name="license.dateOfIssue">
                        {({ field, form, meta: { touched, error } }: FieldProps) => {
                          const showErr = Boolean(error) && (touched || form.submitCount > 0);
                          return (
                          <>
                            <FieldLabel label={t("root:drivingLicenceIssueDate")} required={requiredMap.dateOfIssue} hasError={showErr} />
                            <div className={`${classes.inputBorderWrap} ${showErr ? classes.inputBorderWrapError : ""}`}>
                              <FlatPickerBar
                                enableTime={false}
                                handleDateChange={(value: Date) => {
                                  if (!isNaN(value.getTime())) {
                                    form.setFieldValue("license.dateOfIssue", d.fromJSDate(value).toUTC().toISO()!);
                                  } else {
                                    form.setFieldValue("license.dateOfIssue", "");
                                  }
                                }}
                                identifier="dateOfIssue"
                                placeholderValue={t("root:drivingLicenceIssueDate")}
                                value={field.value ? getLocalizedDateFormat(country, field.value, DATE_TYPE.CONDENSED) : ""}
                                maxDate={getLocalizedDateFormat(country, d.now().toUTC().toISO(), DATE_TYPE.CONDENSED)}
                                country={country}
                                disableWrapper={true}
                              />
                            </div>
                            <Typography variant="body1" className={classes.inputError}>{showErr ? error : null}</Typography>
                          </>
                          );
                        }}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Licence Expiry Date */}
                  {visibilityMap.dateOfExpiry && (
                    <Grid item xs={12} sm={6}>
                      <Field name="license.dateOfExpiry">
                        {({ field, form, meta: { touched, error } }: FieldProps) => {
                          const showErr = Boolean(error) && (touched || form.submitCount > 0);
                          return (
                          <>
                            <FieldLabel label={t("root:drivingLicenceExpiryDate")} required={requiredMap.dateOfExpiry} hasError={showErr} />
                            <div className={`${classes.inputBorderWrap} ${showErr ? classes.inputBorderWrapError : ""}`}>
                              <FlatPickerBar
                                enableTime={false}
                                handleDateChange={(value: Date) => {
                                  if (!isNaN(value.getTime())) {
                                    form.setFieldValue("license.dateOfExpiry", d.fromJSDate(value).toUTC().toISO()!);
                                  } else {
                                    form.setFieldValue("license.dateOfExpiry", "");
                                  }
                                }}
                                identifier="drivingLicenceExpiryDate"
                                placeholderValue={t("root:drivingLicenceExpiryDate")}
                                value={field.value ? getLocalizedDateFormat(country, field.value, DATE_TYPE.CONDENSED) : ""}
                                minDate={getLocalizedDateFormat(country, d.now().toUTC().toISO(), DATE_TYPE.CONDENSED)}
                                country={country}
                                disableWrapper={true}
                              />
                            </div>
                            <Typography variant="body1" className={classes.inputError}>{showErr ? error : null}</Typography>
                          </>
                          );
                        }}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ PCO Issue Date */}
                  {visibilityMap.pcoIssueDate && (
                    <Grid item xs={12} sm={6}>
                      <Field name="pcoIssueDate">
                        {({ field, form, meta: { touched, error } }: FieldProps) => {
                          const showErr = Boolean(error) && (touched || form.submitCount > 0);
                          return (
                          <>
                            <FieldLabel label={t("root:pcoIssueDate")} required={requiredMap.pcoIssueDate} hasError={showErr} />
                            <div className={`${classes.inputBorderWrap} ${showErr ? classes.inputBorderWrapError : ""}`}>
                              <FlatPickerBar
                                enableTime={false}
                                handleDateChange={(value: Date) => {
                                  if (!isNaN(value.getTime())) {
                                    form.setFieldValue("pcoIssueDate", d.fromJSDate(value).toUTC().toISO()!);
                                  } else {
                                    form.setFieldValue("pcoIssueDate", "");
                                  }
                                }}
                                identifier="pcoIssueDate"
                                placeholderValue={t("root:pcoIssueDate")}
                                value={field.value ? getLocalizedDateFormat(country, field.value, DATE_TYPE.CONDENSED) : ""}
                                maxDate={getLocalizedDateFormat(country, d.now().toUTC().toISO(), DATE_TYPE.CONDENSED)}
                                country={country}
                                disableWrapper={true}
                              />
                            </div>
                            <Typography variant="body1" className={classes.inputError}>{showErr ? error : null}</Typography>
                          </>
                          );
                        }}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ PCO Expiry Date */}
                  {visibilityMap.pcoExpiryDate && (
                    <Grid item xs={12} sm={6}>
                      <Field name="pcoExpiryDate">
                        {({ field, form, meta: { touched, error } }: FieldProps) => {
                          const showErr = Boolean(error) && (touched || form.submitCount > 0);
                          return (
                          <>
                            <FieldLabel label={t("root:pcoExpiryDate")} required={requiredMap.pcoExpiryDate} hasError={showErr} />
                            <div className={`${classes.inputBorderWrap} ${showErr ? classes.inputBorderWrapError : ""}`}>
                              <FlatPickerBar
                                enableTime={false}
                                handleDateChange={(value: Date) => {
                                  if (!isNaN(value.getTime())) {
                                    form.setFieldValue("pcoExpiryDate", d.fromJSDate(value).toUTC().toISO()!);
                                  } else {
                                    form.setFieldValue("pcoExpiryDate", "");
                                  }
                                }}
                                identifier="pcoExpiryDate"
                                placeholderValue={t("root:pcoExpiryDate")}
                                value={field.value ? getLocalizedDateFormat(country, field.value, DATE_TYPE.CONDENSED) : ""}
                                minDate={getLocalizedDateFormat(country, d.now().toUTC().toISO(), DATE_TYPE.CONDENSED)}
                                country={country}
                                disableWrapper={true}
                              />
                            </div>
                            <Typography variant="body1" className={classes.inputError}>{showErr ? error : null}</Typography>
                          </>
                          );
                        }}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Nationality */}
                  {visibilityMap.nationality && (
                    <Grid item xs={12} sm={6}>
                      <Field name="nationality">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:nationality")} required={requiredMap.nationality} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:nationality")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Profession */}
                  {visibilityMap.profession && (
                    <Grid item xs={12} sm={6}>
                      <Field name="profession">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:profession")} required={requiredMap.profession} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:enterYourProfession")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Passport Number */}
                  {visibilityMap.passportNumber && (
                    <Grid item xs={12} sm={6}>
                      <Field name="passportNumber">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:passportNumber")} required={requiredMap.passportNumber} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:enterPassportNumber")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Address */}
                  {visibilityMap.location && (
                    <Grid item xs={12} sm={6}>
                      <FieldLabel
                        label={t("root:address")}
                        required={requiredMap.location}
                        hasError={
                          formikProps.touched?.location?.fullAddress &&
                          Boolean(formikProps.errors?.location)
                        }
                      />
                      <AddressPicker
                        fieldName={"location"}
                        onChange={(address: IAddress) => {
                          if (address) formikProps.setFieldValue('location', address);
                        }}
                        required={requiredMap.location}
                        hideInternalLabel={true}
                      />
                    </Grid>
                  )}

                  {/* ✅ PCO Number */}
                  {visibilityMap.customerPCONumber && (
                    <Grid item xs={12} sm={6}>
                      <Field name="customerPCONumber">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:pcoPrivateHiresHireDLNo")} required={requiredMap.customerPCONumber} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:pcoPrivateHiresHireDLNo")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ National Insurance Number */}
                  {visibilityMap.nationalInsuranceNumber && (
                    <Grid item xs={12} sm={6}>
                      <Field name="nationalInsuranceNumber">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:nationalInsuranceNumber")} required={requiredMap.nationalInsuranceNumber} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:enterNationalInsuranceNumber")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                  {/* ✅ Account Number */}
                  {visibilityMap.accountNumber && (
                    <Grid item xs={12} sm={6}>
                      <Field name="accountNumber">
                        {({ field, meta: { touched, error } }: FieldProps) => (
                          <>
                            <FieldLabel label={t("root:accountNumber")} required={requiredMap.accountNumber} hasError={touched && Boolean(error)} />
                            <StyledInput
                              variant="outlined"
                              {...field}
                              placeholder={t("root:accountNumber")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body1" className={classes.inputError}>{touched && error}</Typography>
                          </>
                        )}
                      </Field>
                    </Grid>
                  )}

                </Grid>
              }

              <div className={styles.bottomBar}>
                <Hidden smDown>
                  <div style={{ display: 'flex', flex: 3 }}></div>
                </Hidden>
                <div className="flex fill cross-center main-end" style={{ backgroundColor: '#fff' }}>
                  <button className={styles.prevStepButton} onClick={() => props.onPrevious(formikProps.values)}>
                    <KeyboardArrowLeftIcon />
                    <span className="padding-right">{t("misc:back")}</span>
                  </button>
                  <Hidden mdUp>
                    <button className={`${styles.nextStepButton} ${styles.mobile}`} type="submit" id="getQuoteBtn">
                      {creatingCustomer ? <WhiteCircularProgress size="small" thickness={5} /> : t('root:continue')}
                    </button>
                  </Hidden>
                  <Hidden smDown>
                    <button className={styles.nextStepButton} type="submit" id="getQuoteBtn">
                      {creatingCustomer
                        ? <WhiteCircularProgress size="small" thickness={5} />
                        : getNextStepText(props.bookingData?.bookingCategory || BookingType.RENTAL)
                      }
                    </button>
                  </Hidden>
                </div>
              </div>
            </Grid>

            <Grid item container xs={12}>
              {(user && (user?.customer || user.businesscustomers) && additionalDriverQuantity) ? (
                <Grid item container>
                  <Grid xs={12}>
                    <SearchForm
                      updateData={addExistingDriver}
                      dataOptions={user.customer?.approvedDrivers ? user.customer?.approvedDrivers : user?.businesscustomers?.approvedDrivers}
                    />
                  </Grid>
                </Grid>
              ) : <></>}
              {(user?.businesscustomers || user?.customer?.id) && driverList?.length ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("root:name")}</TableCell>
                        <TableCell>{t("root:email")}</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>{t("root:action")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {driverList.map((driver: IDriverRow, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{`${driver.firstName} ${driver.lastName}`}</TableCell>
                          <TableCell>{driver.email}</TableCell>
                          <TableCell style={{ textAlign: 'center' }}>
                            <IconButton color="secondary" aria-label="delete" onClick={() => handleRemoveDriver(driver)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : <></>}
            </Grid>

            <Grid item container xs={12}>
              {(mergedPreBookingQuestions && mergedPreBookingQuestions?.length) ? (
                <Grid item xs={12} style={{ margin: '40px 0 30px 0' }}>
                  <Typography variant="h4" align="center" style={{ fontWeight: 700 }}>
                    {t("root:additionalInformation")}
                  </Typography>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                {(mergedPreBookingQuestions && mergedPreBookingQuestions?.length)
                  ? mergedPreBookingQuestions.map((bookingQuestion, idx) => {
                    switch (bookingQuestion.answerType) {
                      case ANSWER_TYPES.CHECKBOX:
                        return (
                          <Field key={idx} name={`preBookingQuestions[${idx}].answer`} validate={(value: any) => validateAnswer(value, bookingQuestion.required, 'TRUE')}>
                            {({ field, meta: { touched, error } }: FieldProps) => (
                              <div className={`${styles.validation} ${(touched && Boolean(error)) ? styles.error : ''}`}>
                                <FormControlLabel
                                  control={
                                    <ColoredCheckbox
                                      {...field}
                                      checked={field.value}
                                      size="small"
                                      color="primary"
                                      inputProps={{ 'aria-label': 'secondary checkbox' }}
                                      disabled={bookingQuestion.deleted}
                                    />
                                  }
                                  label={`${bookingQuestion.question} ${bookingQuestion.required ? "*" : ""}`}
                                />
                                {touched && Boolean(error) && <Typography variant="body1" style={{ color: '#D13135' }}>{bookingQuestion.errorMessage}</Typography>}
                              </div>
                            )}
                          </Field>
                        );

                      case ANSWER_TYPES.DROP_DOWN:
                        return (
                          <Field key={idx} name={`preBookingQuestions[${idx}].answer`} validate={(value: any) => validateAnswer(value, bookingQuestion.required, "TEXT")}>
                            {({ field, meta: { touched, error } }: FieldProps) => (
                              <div className={`${styles.bookingQuestion} ${touched && Boolean(error) ? styles.error : ''}`}>
                                <div className={styles.rowFlex}>
                                  <div style={{ display: 'flex', flex: 1, paddingRight: 15 }}>
                                    <Typography>{`${bookingQuestion.question} ${bookingQuestion.required ? "*" : ""}`}</Typography>
                                  </div>
                                  <div className={styles.dropdownWrap}>
                                    <StyledInput
                                      select
                                      {...field}
                                      fullWidth
                                      variant="outlined"
                                      error={touched && Boolean(error)}
                                      disabled={bookingQuestion.deleted}
                                    >
                                      {bookingQuestion.options && bookingQuestion.options.map((option) => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                      ))}
                                    </StyledInput>
                                  </div>
                                </div>
                                {touched && Boolean(error) && <Typography variant="body1" style={{ color: '#D13135' }}>{bookingQuestion.errorMessage}</Typography>}
                              </div>
                            )}
                          </Field>
                        );

                      case ANSWER_TYPES.YES_NO:
                        return (
                          <Field key={idx} name={`preBookingQuestions[${idx}].answer`} validate={(value: any) => validateAnswer(value, bookingQuestion.required, bookingQuestion.expectedAnswer)}>
                            {({ field, meta: { touched, error } }: FieldProps) => (
                              <RadioGroup {...field} onClick={field.onBlur}>
                                <div className={`${styles.bookingQuestion} ${touched && Boolean(error) ? styles.error : ''}`}>
                                  <div className={styles.rowFlex}>
                                    <div style={{ display: 'flex', flex: 1, paddingRight: 15 }}>
                                      <Typography>{`${bookingQuestion.question} ${bookingQuestion.required ? "*" : ""}`}</Typography>
                                    </div>
                                    <div className={styles.yesnoWrap}>
                                      <Radio value="YES" id={`yes_no_${idx}_yes`} style={{ display: 'none' }} disabled={bookingQuestion.deleted} />
                                      <label htmlFor={`yes_no_${idx}_yes`} className={`${styles.yesnoButton} ${styles.left} ${field.value === "YES" ? styles.active : ''} ${bookingQuestion.deleted ? styles.disabled : ''}`}>{t("misc:yes")}</label>
                                      <Radio value="NO" id={`yes_no_${idx}_no`} style={{ display: 'none' }} disabled={bookingQuestion.deleted} />
                                      <label htmlFor={`yes_no_${idx}_no`} className={`${styles.yesnoButton} ${styles.right} ${field.value === "NO" ? styles.active : ''} ${bookingQuestion.deleted ? styles.disabled : ''}`}>{t("misc:no")}</label>
                                    </div>
                                  </div>
                                  {touched && Boolean(error) && <Typography variant="body1" style={{ color: '#D13135' }}>{bookingQuestion.errorMessage}</Typography>}
                                </div>
                              </RadioGroup>
                            )}
                          </Field>
                        );

                      default:
                        return null;
                    }
                  })
                  : null}

                {(mergedPreBookingQuestions && mergedPreBookingQuestions?.length)
                  ? mergedPreBookingQuestions.map((bookingQuestion, idx) => {
                    if (bookingQuestion.answerType === ANSWER_TYPES.TEXT) {
                      return (
                        <Field key={idx} name={`preBookingQuestions[${idx}].answer`} validate={(value: any) => validateAnswer(value, bookingQuestion.required, 'TEXT')}>
                          {({ field, meta: { touched, error } }: FieldProps) => (
                            <>
                              <Typography variant="body1">{`${bookingQuestion.question} ${bookingQuestion.required ? "*" : ""}`}</Typography>
                              <StyledInput
                                variant="outlined"
                                {...field}
                                multiline
                                fullWidth
                                error={touched && Boolean(error)}
                                disabled={bookingQuestion.deleted}
                              />
                              <Typography variant="body1" style={{ color: '#D13135', padding: '5px 15px' }}>
                                {touched && Boolean(error) && bookingQuestion.errorMessage}
                              </Typography>
                            </>
                          )}
                        </Field>
                      );
                    }
                    return null;
                  })
                  : null}

                {authState.accountType === AccountType.INDIVIDUAL && user?.customer && (
                  <Grid item xs={12} sm={6} md={8} lg={8} xl={8}>
                    <Box display="flex" gridGap={16} flexGrow={1}>
                      {organisation.enableProjectIds && (
                        <Autocomplete
                          id="free-solo-demo"
                          options={projectIds}
                          getOptionLabel={(option: any) => option}
                          onInputChange={(_, value) => setQueryInputValue(value)}
                          onChange={(_event: any, value: any, reason) => {
                            if (reason === "clear" || reason === "remove-option") {
                              setProjectId("");
                              setCostCenter("");
                            }
                            if (reason === "select-option") {
                              let costCenter = "";
                              if (user.customer && user.customer.costCenters && user.customer.costCenters.length) {
                                costCenter = user.customer.costCenters[0].name;
                              }
                              setProjectId(value);
                              setCostCenter(costCenter);
                            }
                          }}
                          noOptionsText={
                            queryInputValue.length > 2 && !projectIds.length
                              ? "No project ids found"
                              : "Type to search project ids"
                          }
                          value={projectId}
                          style={{ width: "100%" }}
                          loading={loadingProjectIds}
                          loadingText="Loading Project IDs..."
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Search Project IDs"
                              name="projectId"
                              placeholder="Search Project ID"
                              margin="normal"
                              variant="outlined"
                              style={{ backgroundColor: "white" }}
                            />
                          )}
                        />
                      )}
                      {organisation.enableCostCenter && user.customer?.costCenters && user.customer.costCenters.length > 0 && (
                        <Box display="flex" flexGrow={1} alignItems="center" style={{ width: "100%" }}>
                          <Typography variant="h4" style={{ fontWeight: 500 }}>
                            Cost Center: {user.customer.costCenters[0].name}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                )}

                <Field name="customerNote">
                  {({ field, meta: { touched, error } }: FieldProps) => (
                    <>
                      <FieldLabel label={`${getLocalizedBookingSyntex(country)} ${t("root:note")}`} />
                      <StyledInput
                        variant="outlined"
                        {...field}
                        multiline
                        fullWidth
                        error={touched && Boolean(error)}
                        style={{ margin: '10px 0 30px 0' }}
                      />
                    </>
                  )}
                </Field>

                <Field name="agreeToTerms" validate={(value: any) => validateAnswer(value, true, 'TRUE')}>
                  {({ field, meta: { touched, error } }: FieldProps) => (
                    <div className={`${(touched && Boolean(error)) ? styles.error : ''}`} style={{ marginTop: 40, marginLeft: 3 }}>
                      <FormControlLabel
                        control={
                          <ColoredCheckbox
                            {...field}
                            size="small"
                            color="primary"
                            inputProps={{ 'aria-label': 'secondary checkbox' }}
                          />
                        }
                        label={t("root:agreeWithTermsAndConditions")}
                      />
                      <a rel="noreferrer" target="_blank" className={styles.footerUrl} href={website.termsAndConditionsURL}>
                        <div>
                          <Hidden smDown><span style={{ paddingRight: 5 }}>{t("root:termsAndConditions")}</span></Hidden>
                          <LaunchIcon style={{ fontSize: 16 }} />
                        </div>
                      </a>
                      {(touched && Boolean(error)) && (
                        <Typography variant="body1" style={{ color: '#D13135' }}>
                          {t("validation:termsConditionsAccepted")}
                        </Typography>
                      )}
                    </div>
                  )}
                </Field>
                <div style={{ marginTop: 20 }}></div>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default SimpleCustomerView;
